import { LoadingSpinner } from '@workspace/ui/components/loading-spinner'
import { Check } from 'lucide-react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import type { ChatMessage } from '../types'
import { AgentAvatar } from './agent-avatar'
import { ConfirmInfoCard } from './confirm-info-card'
import { MultiSelectCard } from './multi-select-card'
import { RoleSelectCard } from './role-select-card'
import { SearchingIndicator } from './searching-indicator'

interface InfoSection {
  title: string
  items: { label: string; value: string }[]
}

interface MessageBubbleProps {
  message: ChatMessage
  onChoice?: (messageId: string, value: string) => void
  onConfirm?: (messageId: string, editedSections?: InfoSection[]) => void
  onMultiSelect?: (messageId: string, values: string[]) => void
}

export function MessageBubble({ message, onChoice, onConfirm, onMultiSelect }: MessageBubbleProps) {
  if (message.role === 'user') {
    if (message.type === 'file-upload-status') {
      return (
        <div className="flex justify-end">
          <div className="flex items-center gap-2 rounded-2xl rounded-br-md bg-[#f97316] px-4 py-2.5">
            {message.isUploading ? (
              <LoadingSpinner size="sm" className="text-white" />
            ) : (
              <Check className="size-4 text-white" />
            )}
            <span className="text-sm text-white">{message.content}</span>
          </div>
        </div>
      )
    }

    return (
      <div className="flex justify-end">
        <div className="max-w-[80%] rounded-2xl rounded-br-md bg-[#f97316] px-4 py-2.5">
          <div className="chat-prose prose prose-sm max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
            <Markdown remarkPlugins={[remarkGfm]}>{message.content}</Markdown>
          </div>
        </div>
      </div>
    )
  }

  // Hidden routing-only message — not rendered
  if (message.type === 'identity-prompt') {
    return null
  }

  if (message.type === 'confirm-info' && (message.infoCards || message.infoSections)) {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex gap-3">
          <AgentAvatar />
          <div className="rounded-2xl rounded-tl-md border border-border bg-card px-4 py-2.5">
            <div className="chat-prose prose prose-sm max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
              <Markdown remarkPlugins={[remarkGfm]}>{message.content}</Markdown>
            </div>
          </div>
        </div>
        <div className="pl-11">
          <ConfirmInfoCard
            items={message.infoCards}
            sections={message.infoSections}
            resolved={message.resolved}
            onConfirm={(editedSections) => onConfirm?.(message.id, editedSections)}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="flex gap-3">
      <AgentAvatar />
      <div className="flex min-w-0 max-w-[85%] flex-col gap-2">
        {(message.type === 'text' || message.type === 'text-input') && (
          <div className="rounded-2xl rounded-tl-md border border-border bg-card px-4 py-2.5">
            <div className="chat-prose prose prose-sm max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
              <Markdown remarkPlugins={[remarkGfm]}>{message.content}</Markdown>
            </div>
          </div>
        )}

        {message.type === 'role-select' && message.choices && (
          <RoleSelectCard
            choices={message.choices}
            resolved={message.resolved}
            selectedValue={message.selectedValue}
            onSelect={(value) => onChoice?.(message.id, value)}
          />
        )}

        {message.type === 'searching' && (
          <SearchingIndicator content={message.content} resolved={message.resolved} />
        )}

        {message.type === 'multi-select' && message.options && (
          <MultiSelectCard
            options={message.options}
            resolved={message.resolved}
            selectedValues={message.selectedValues}
            onSubmit={(values) => onMultiSelect?.(message.id, values)}
          />
        )}
      </div>
    </div>
  )
}
